<script setup lang="ts">
import { type Component, computed } from "vue";

import Button from "~/components/dumb/Button.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { EMPTY_STATE_ICON_GRADIENT_BY_THEME } from "~/constants/style";
import { ButtonSize, ButtonStyle, CommandId, IconSize } from "~/shared/enums";
import { usePageStore } from "~/stores";

defineProps<{
  mainIcon: Component;
  title: string;
  description?: string;
  buttonIcon?: Component;
  buttonText?: string;
  buttonCommand?: CommandId;
  hideButton?: boolean;
  inline?: boolean;
}>();

const emit = defineEmits<{
  click: [];
}>();

const pageStore = usePageStore();

const iconBg = computed(() => EMPTY_STATE_ICON_GRADIENT_BY_THEME[pageStore.theme]);
</script>

<template>
  <div
    class="flex h-full items-center"
    :class="
      inline ? 'justify-center px-4 pb-6' : 'pointer-events-none absolute inset-0 w-full flex-col justify-evenly'
    ">
    <div class="flex flex-col items-center gap-6" :class="!inline && 'w-64'">
      <div
        class="dart-icon-grad-bg flex shrink-0 items-center justify-center rounded-lg border border-md p-3 shadow-lg shadow-opposite/10">
        <component :is="mainIcon" class="size-10 text-lt" />
      </div>
      <div class="flex w-full flex-col items-center justify-center gap-1">
        <p class="text-center select-none" :class="inline ? 'text-lg text-vlt' : 'font-semibold text-md'">
          {{ title }}
        </p>
        <p v-if="description" class="w-full break-words text-center text-sm hyphens-auto text-lt select-none">
          {{ description }}
        </p>
      </div>
      <div v-if="!inline" class="flex items-center gap-4">
        <slot name="extraButton" />
        <Tooltip v-if="buttonText" :disabled="!buttonCommand" :command-id="buttonCommand">
          <Button
            v-if="!hideButton"
            :btn-style="ButtonStyle.PRIMARY"
            :size="ButtonSize.SMALL"
            :icon-size="IconSize.S"
            :text="buttonText"
            data-testid="empty-state-button"
            :icon="buttonIcon"
            class="pointer-events-auto"
            :class="buttonIcon && 'pl-2'"
            @click="emit('click')" />
        </Tooltip>
      </div>
    </div>
    <div />
  </div>
</template>

<style scoped>
.dart-icon-grad-bg {
  background: v-bind(iconBg);
}
</style>
